import React, { useEffect } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Stack, xcss } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import Button from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { ONBOARDING_POPULAR_FEED_CARD_SEEN_KEY } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';

import PopularOnboardingImageDark from './images/PopularOnboardingMessageDark.svg';
import PopularOnboardingImageLight from './images/PopularOnboardingMessageLight.svg';

export const i18n = defineMessages({
	headerTitle: {
		id: 'feed.popular.header.title',
		defaultMessage: 'Nothing’s trending... yet! 🚀',
		description: 'Title header from Popular onboarding message',
	},
	headerDescription: {
		id: 'feed.popular.header.description',
		defaultMessage:
			'Looks like there’s no popular content here—for now! Popular content is determined by views and engagement, so check back soon or shape what rises to the top by engaging with content via comments and reactions!',
		description:
			"Description for popular onboarding message. 'they' being the people followed from the title message",
	},
	closeMessageLabel: {
		id: 'feed.popular.header.closeLabel',
		defaultMessage: 'Close message box',
		description: 'label for the close icon to dismiss the message box',
	},
});

const bodyContainerStyles = xcss({
	boxSizing: 'border-box',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderWidth: 'border.width',
	borderRadius: 'radius.small',
	background: 'elevation.surface',
	display: 'flex',
	marginBottom: 'space.300',
	padding: 'space.300',
	paddingBottom: 'space.0',
});

const messageContainerStyles = xcss({
	flexDirection: 'column',
	flexGrow: 1,
	marginRight: 'space.250',
	marginBottom: 'space.300',
});

const descriptionTextStyles = xcss({
	marginTop: 'space.100',
	marginBottom: 'space.300',
});

const onboardingImageStyles = xcss({
	width: '200px',
	flexShrink: 0,
	alignSelf: 'flex-end',
	marginRight: 'space.100',
});

const closeButtonContainerStyles = xcss({
	flexShrink: 0,
});

export const PopularOnboardingMessage = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setOnboardingState } = useSetOnboardingState();
	const intl = useIntl();

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error,
	} = useGetOnboardingState(ONBOARDING_POPULAR_FEED_CARD_SEEN_KEY);

	const { isDismissed } = deserializeState(onboardingStateData);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'PopularOnboardingMessage',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const stop = () => {
		void setOnboardingState({
			key: ONBOARDING_POPULAR_FEED_CARD_SEEN_KEY,
			value: 'true',
		});
	};

	if (onboardingStateLoading || isDismissed) {
		return null;
	}

	return (
		<>
			<Box xcss={bodyContainerStyles}>
				<Stack xcss={messageContainerStyles}>
					<Heading size="medium">
						<FormattedMessage {...i18n.headerTitle} />
					</Heading>
					<Box xcss={descriptionTextStyles}>
						<FormattedMessage {...i18n.headerDescription} />
					</Box>
				</Stack>

				<Box xcss={onboardingImageStyles}>
					<Image
						src={PopularOnboardingImageLight}
						srcDark={PopularOnboardingImageDark}
						width="200"
						height="160"
						alt=""
					/>
				</Box>
				<Box xcss={closeButtonContainerStyles}>
					<Button appearance="subtle" onClick={stop}>
						<CrossIcon label={intl.formatMessage(i18n.closeMessageLabel)} />
					</Button>
				</Box>
			</Box>
			{error && <ErrorDisplay error={error} />}
		</>
	);
};
